import React, {
  useState, useEffect, useCallback,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Gift, Clock, CheckCircle2, XCircle, Trophy,
} from 'lucide-react';

import Button from '../../components/ui/Button';
import TextInput from '../../components/ui/TextInput.jsx';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import {
  getMyRaffles, enterRaffle, claimRaffle,
} from '../raffles/config';

// Çekilişlerim — çalışan self-servisi. Katılım bir NESNE oluşturur, bu yüzden form
// GenericFormDrawer ile açılır (UI altın kuralı: veri girişinde modal kullanılmaz).
const Container = styled.div``;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`;

const Cover = styled.div`
  height: 132px;
  background: ${({ $url, theme }) => ($url
    ? `url(${$url}) center/cover no-repeat`
    : `linear-gradient(135deg, ${theme.colors.primary.main}, ${theme.colors.secondary.main})`)};
`;

const Body = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const Title = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Desc = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  align-self: flex-start;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: auto;
  padding-top: 10px;
  flex-wrap: wrap;
`;

const WinnerList = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const EmptyBox = styled.div`
  padding: 48px 16px;
  text-align: center;
  color: #6c757d;
  font-size: 13px;
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const statusBadge = (r, t) => {
  if (r.myOutcome === 'winner') {
    return { text: t('raffle.my.won'), bg: 'rgba(2,211,191,0.12)', color: '#01A899', Icon: Trophy };
  }
  if (r.myOutcome === 'backup') {
    return { text: t('raffle.my.backup'), bg: 'rgba(255,193,7,0.14)', color: '#C79100', Icon: Gift };
  }
  if (r.status === 'drawn' && r.hasEntered) {
    return { text: t('raffle.my.notWon'), bg: 'rgba(102,102,119,0.10)', color: '#667', Icon: XCircle };
  }
  if (r.hasEntered) {
    return { text: t('raffle.my.entered'), bg: 'rgba(2,145,239,0.10)', color: '#0291EF', Icon: CheckCircle2 };
  }
  return null;
};

export default function MyRafflesPage() {
  const { t } = useTranslation();
  const [raffles, setRaffles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entering, setEntering] = useState(null); // katılım drawer'ı açık olan çekiliş
  const [answers, setAnswers] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await getMyRaffles();
    setRaffles(res.data?.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEnter = useCallback((raffle) => {
    setAnswers({});
    setEntering(raffle);
  }, []);

  const handleEnter = useCallback(async () => {
    if (!entering) return false;
    const payload = {
      answers: (entering.questions || []).map((q) => ({
        questionId: q.id,
        answerText: answers[q.id] || '',
      })),
    };
    const res = await enterRaffle(entering.id, payload);
    if (!res?.data?.status) return false;
    await load();
    return true;
  }, [entering, answers, load]);

  const handleClaim = useCallback(async (raffle, decision) => {
    setBusyId(raffle.id);
    const res = await claimRaffle(raffle.id, decision);
    setBusyId(null);
    if (res?.data?.status) await load();
  }, [load]);

  if (loading) return <Container><EmptyBox>{t('common.loading')}</EmptyBox></Container>;
  if (raffles.length === 0) return <Container><EmptyBox>{t('raffle.my.empty')}</EmptyBox></Container>;

  return (
    <Container>
      <GenericFormDrawer
        open={!!entering}
        onClose={() => setEntering(null)}
        title={t('raffle.my.enterTitle', { title: entering?.title || '' })}
        schema={[]}
        onSave={handleEnter}
      >
        {(entering?.questions || []).map((q) => (
          <FieldGroup key={q.id}>
            <TextInput
              label={q.text}
              value={answers[q.id] || ''}
              onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
              required={q.isRequired}
            />
          </FieldGroup>
        ))}
        {(entering?.questions || []).length === 0 && (
          <Desc>{t('raffle.my.noQuestionsHint')}</Desc>
        )}
      </GenericFormDrawer>

      <Grid>
        {raffles.map((r) => {
          const badge = statusBadge(r, t);
          return (
            <Card key={r.id} data-role="raffle-card">
              <Cover $url={r.imageUrl} />
              <Body>
                <Title>{r.title}</Title>
                {r.description && <Desc>{r.description}</Desc>}
                <Meta>
                  <Clock size={13} />
                  {t('raffle.my.deadline')}: {new Date(r.entryDeadline).toLocaleDateString()}
                </Meta>
                <Meta>
                  <Gift size={13} />
                  {t('raffle.my.prizeInfo', { winners: r.winnerCount, items: r.itemsPerWinner })}
                </Meta>
                {badge && (
                  <Badge $bg={badge.bg} $color={badge.color}>
                    <badge.Icon size={13} /> {badge.text}
                  </Badge>
                )}
                {r.winnerNames?.length > 0 && (
                  <WinnerList>
                    {t('raffle.my.winners')}: {r.winnerNames.join(', ')}
                  </WinnerList>
                )}

                <Actions>
                  {!r.hasEntered && r.isEntryOpen && (
                    <Button data-role="enter-btn" onClick={() => openEnter(r)}>
                      {t('raffle.my.enter')}
                    </Button>
                  )}
                  {r.canClaim && (
                    <>
                      <Button
                        data-role="accept-btn"
                        disabled={busyId === r.id}
                        onClick={() => handleClaim(r, 'accept')}
                      >
                        {t('raffle.my.accept')}
                      </Button>
                      <Button
                        variant="outline"
                        data-role="decline-btn"
                        disabled={busyId === r.id}
                        onClick={() => handleClaim(r, 'decline')}
                      >
                        {t('raffle.my.decline')}
                      </Button>
                    </>
                  )}
                </Actions>
              </Body>
            </Card>
          );
        })}
      </Grid>
    </Container>
  );
}
